import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { loadGoogleMaps } from '@/utils/loadGoogleMaps';

const CITIES = {
  'Madrid': { lat: 40.4168, lng: -3.7038 },
  'Barcelona': { lat: 41.3851, lng: 2.1734 },
  'Valencia': { lat: 39.4699, lng: -0.3763 },
  'Sevilla': { lat: 37.3891, lng: -5.9845 },
  'Málaga': { lat: 36.7213, lng: -4.4214 },
  'Bilbao': { lat: 43.2630, lng: -2.9350 },
  'Alicante': { lat: 38.3452, lng: -0.4810 },
  'Zaragoza': { lat: 41.6488, lng: -0.8891 }
};

const SPAIN_BOUNDS = { north: 43.8, south: 35.9, west: -9.4, east: 4.4 };
const SUGGESTIONS_CLASS = 'autogo-address-suggestions';

function latLngOf(location) {
  if (!location) return null;
  const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
  if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return null;
  return { lat: Number(lat), lng: Number(lng) };
}

function predictionLabel(pred, field) {
  const value = pred?.[field];
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value.toString === 'function') return value.toString();
  return value.text || '';
}

const AddressAutocomplete = ({ onChange, placeholder, dataTestId, value, inputClassName, embedOnDark = false }) => {
  const { t, i18n } = useTranslation();
  const [address, setAddress] = useState(value || '');
  const [showManual, setShowManual] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [mapsFailed, setMapsFailed] = useState(false);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const sessionTokenRef = useRef(null);
  const placesLibRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const debounceRef = useRef(null);
  const mapsWarnedRef = useRef(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const emitChange = useCallback((payload) => {
    onChangeRef.current?.(payload);
  }, []);

  const refreshSessionToken = useCallback((PlacesLib) => {
    const Token = PlacesLib?.AutocompleteSessionToken;
    sessionTokenRef.current = Token ? new Token() : null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(async () => {
        if (cancelled) return;
        const lib = await window.google.maps.importLibrary('places');
        if (cancelled) return;
        if (!lib?.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
          throw new Error('Places AutocompleteSuggestion is unavailable');
        }
        placesLibRef.current = lib;
        refreshSessionToken(lib);
        setMapsFailed(false);
      })
      .catch((err) => {
        console.warn('Address autocomplete: Google Places (New) failed to load:', err?.message || err);
        if (!cancelled) {
          setMapsFailed(true);
          if (!mapsWarnedRef.current) {
            mapsWarnedRef.current = true;
            toast.error(t('address_toast_init_error'));
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshSessionToken, t]);

  useEffect(() => {
    if (value !== undefined) {
      setAddress(value);
    }
  }, [value]);

  const updateDropdownPos = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!showSuggestions) return undefined;
    updateDropdownPos();
    const onReposition = () => updateDropdownPos();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [showSuggestions, updateDropdownPos]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (suggestionsRef.current?.contains(target) || inputRef.current?.contains(target)) return;
      setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    const seq = ++fetchSeqRef.current;
    const PlacesLib = placesLibRef.current;
    if (!PlacesLib?.AutocompleteSuggestion || input.trim().length < 2) {
      if (seq === fetchSeqRef.current) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
      return;
    }
    try {
      if (!sessionTokenRef.current) refreshSessionToken(PlacesLib);
      const language = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'es';
      const { suggestions: raw } = await PlacesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: input.trim(),
        sessionToken: sessionTokenRef.current || undefined,
        language,
        region: 'es',
        locationBias: SPAIN_BOUNDS,
      });
      if (seq !== fetchSeqRef.current) return;
      const mapped = (raw || [])
        .map((item) => item?.placePrediction)
        .filter(Boolean)
        .map((pred) => ({
          placeId: pred.placeId,
          description: predictionLabel(pred, 'text'),
          mainText: predictionLabel(pred, 'mainText') || predictionLabel(pred, 'text'),
          secondaryText: predictionLabel(pred, 'secondaryText'),
          placePrediction: pred,
        }));
      setSuggestions(mapped);
      setShowSuggestions(mapped.length > 0);
      if (mapped.length > 0) updateDropdownPos();
    } catch (err) {
      console.warn('Address autocomplete suggestions failed:', err);
      if (seq !== fetchSeqRef.current) return;
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [i18n.language, refreshSessionToken, updateDropdownPos]);

  const handleCitySelect = (city) => {
    const coords = CITIES[city];
    const cityAddress = t('address_city_country_suffix', { city });
    setAddress(cityAddress);
    setShowSuggestions(false);
    emitChange({
      address: cityAddress,
      lat: coords.lat,
      lng: coords.lng
    });
    toast.success(t('address_toast_city_selected', { city }));
  };

  const handleManualCoords = () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);

    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      const coordsAddress = address || `Coordenadas: ${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      setAddress(coordsAddress);
      emitChange({
        address: coordsAddress,
        lat: lat,
        lng: lng
      });
      toast.success(t('address_toast_coords_set'));
      setShowManual(false);
      setManualLat('');
      setManualLng('');
    } else {
      toast.error(t('address_toast_invalid_coords'));
    }
  };

  const extractCoordinatesFromLink = (link) => {
    try {
      let lat;
      let lng;
      const atMatch = link.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (atMatch) {
        lat = parseFloat(atMatch[1]);
        lng = parseFloat(atMatch[2]);
      } else {
        const qMatch = link.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (qMatch) {
          lat = parseFloat(qMatch[1]);
          lng = parseFloat(qMatch[2]);
        }
      }

      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        emitChange({
          address: t('address_coords_from_maps', { lat: lat.toFixed(4), lng: lng.toFixed(4) }),
          lat: lat,
          lng: lng
        });
        setAddress(t('address_maps_short_label', { lat: lat.toFixed(4), lng: lng.toFixed(4) }));
        toast.success(t('address_toast_coords_extracted'));
      } else {
        toast.error(t('address_toast_coords_extract_error'));
      }
    } catch (error) {
      console.error('Error extracting coordinates from link:', error);
    }
  };

  const handleAddressChange = (e) => {
    const input = e.target.value;
    setAddress(input);

    if (input.includes('google.com/maps') || input.includes('goo.gl/maps') || input.includes('maps.app.goo.gl')) {
      extractCoordinatesFromLink(input);
      setShowSuggestions(false);
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (input.trim().length < 2 || mapsFailed) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(input);
    }, 280);
  };

  const applyPlace = async (placePrediction, fallbackDescription) => {
    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ['formattedAddress', 'location', 'displayName'],
      });
      const coords = latLngOf(place.location);
      if (!coords) {
        toast.error(t('address_toast_no_coords'));
        return;
      }
      const selectedAddress = place.formattedAddress || place.displayName || fallbackDescription;
      setAddress(selectedAddress);
      emitChange({
        address: selectedAddress,
        lat: coords.lat,
        lng: coords.lng
      });
      toast.success(t('address_toast_selected'));
      refreshSessionToken(placesLibRef.current);
    } catch (err) {
      console.error('Place details failed:', err);
      toast.error(t('address_toast_error_get_details'));
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    setSuggestions([]);
    applyPlace(suggestion.placePrediction, suggestion.description);
  };

  const geocodeTypedAddress = async () => {
    try {
      await loadGoogleMaps();
      const { Geocoder } = await window.google.maps.importLibrary('geocoding');
      const geocoder = new Geocoder();
      const { results } = await geocoder.geocode({
        address: address.trim(),
        region: 'es',
      });
      const first = results?.[0];
      const coords = latLngOf(first?.geometry?.location);
      if (!first || !coords) {
        toast.info(t('address_toast_select_suggestions'));
        return;
      }
      const selectedAddress = first.formatted_address || address.trim();
      setAddress(selectedAddress);
      emitChange({
        address: selectedAddress,
        lat: coords.lat,
        lng: coords.lng
      });
      toast.success(t('address_toast_selected'));
    } catch (err) {
      console.warn('Geocode fallback failed:', err);
      toast.info(t('address_toast_select_suggestions'));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (suggestions.length > 0) {
      handleSuggestionClick(suggestions[0]);
      return;
    }
    if (address.trim()) geocodeTypedAddress();
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2 relative">
        <input
          ref={inputRef}
          type="text"
          value={address}
          onChange={handleAddressChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              updateDropdownPos();
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder || t('address_autocomplete_placeholder_default')}
          data-testid={dataTestId}
          autoComplete="off"
          className={cn(
            'flex w-full rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500',
            embedOnDark
              ? 'ag-input min-h-[2.75rem] h-auto border-white/15'
              : 'h-10 border border-gray-300 bg-white text-neutral-900 placeholder:text-neutral-500',
            inputClassName,
          )}
        />

        {showSuggestions && suggestions.length > 0 && createPortal(
          <div
            ref={suggestionsRef}
            className={cn(
              SUGGESTIONS_CLASS,
              'bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto',
            )}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 2147483000,
            }}
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.placeId || index}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSuggestionClick(suggestion);
                }}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-sm text-gray-900">
                  {suggestion.mainText || suggestion.description}
                </div>
                {suggestion.secondaryText ? (
                  <div className="text-xs text-gray-600">
                    {suggestion.secondaryText}
                  </div>
                ) : null}
              </div>
            ))}
          </div>,
          document.body
        )}

        <p
          className={cn(
            'text-xs',
            embedOnDark ? 'text-[#94A3B8]' : 'text-gray-600',
          )}
        >
          {mapsFailed ? t('address_suggestions_unavailable') : t('ph_address_maps')}
        </p>
      </div>

      <div
        className={cn(
          'p-3 rounded-lg border',
          embedOnDark
            ? 'bg-white/[0.06] border-white/15'
            : 'bg-green-50 border-green-200',
        )}
      >
        <p
          className={cn(
            'text-xs font-medium mb-2',
            embedOnDark ? 'text-[#CBD5E1]' : 'text-green-800',
          )}
        >
          {t('address_quick_city_heading')}
        </p>
        <div className="flex flex-wrap gap-2">
          {Object.keys(CITIES).map(city => (
            <Button
              key={city}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleCitySelect(city)}
              className={cn(
                'text-xs',
                embedOnDark
                  ? 'border-[#00F260]/40 text-[#F8FAFC] bg-white/[0.04] hover:bg-[#00F260]/15 hover:text-white'
                  : 'border-green-300 text-green-900 hover:bg-green-100',
              )}
            >
              {city}
            </Button>
          ))}
        </div>
      </div>

      <div className="border-t pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowManual(!showManual)}
          className={cn('w-full text-xs', embedOnDark && 'text-[#94A3B8] hover:text-white hover:bg-white/5')}
        >
          {showManual ? t('address_manual_gps_close') : t('address_manual_gps_open')}
        </Button>

        {showManual && (
          <div
            className={cn(
              'mt-2 space-y-2 p-3 rounded-lg border',
              embedOnDark
                ? 'bg-[#0F172A] border-white/10'
                : 'bg-blue-50 border-blue-200',
            )}
          >
            <p
              className={cn(
                'text-xs font-medium',
                embedOnDark ? 'text-[#94A3B8]' : 'text-blue-800',
              )}
            >
              {t('address_manual_gps_hint')}
            </p>
            <div className="space-y-2">
              <input
                type="number"
                step="0.0001"
                placeholder={t('ph_lat_example')}
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                className={cn(
                  'w-full px-2 py-1 text-sm rounded focus:outline-none focus:ring-2',
                  embedOnDark
                    ? 'ag-input border-white/15 focus:ring-[#00F260]'
                    : 'border border-blue-200 focus:ring-blue-500',
                )}
              />
              <input
                type="number"
                step="0.0001"
                placeholder={t('ph_lon_example')}
                value={manualLng}
                onChange={(e) => setManualLng(e.target.value)}
                className={cn(
                  'w-full px-2 py-1 text-sm rounded focus:outline-none focus:ring-2',
                  embedOnDark
                    ? 'ag-input border-white/15 focus:ring-[#00F260]'
                    : 'border border-blue-200 focus:ring-blue-500',
                )}
              />
              <Button
                type="button"
                size="sm"
                onClick={handleManualCoords}
                disabled={!manualLat || !manualLng}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs"
              >
                {t('address_manual_gps_apply')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressAutocomplete;
